<script>
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'PublishProgress',
  props: {
    visible: Boolean,
    status: String,
    message: String,
    percentComplete: Number,
    queue: Array,
    showQueue: Boolean,
    unpublishedRegions: Array
  },
  methods: {
    getErrorMessage(code) {
      return getErrorMessage(code);
    }
  }
};
</script>

<template>
  <transition name="fade">
    <div v-if="visible" class="publish-progress">
      <div class="publish-progress-body">
        <transition-group name="slide">
          <!-- Progress bar -->
          <div key="progress" class="publish-progress-bar">
            <el-progress
              :percentage="percentComplete"
              :status="status"
              :width="showQueue ? 100 : 200"
              :stroke-width="showQueue ? 2 : 10"
              type="circle"
            >
              {{ percentComplete }}%
            </el-progress>
            <p class="publish-progress-message">
              {{ message }}
            </p>
          </div>

          <!-- Publish queue -->
          <div v-if="showQueue" key="queue" class="publish-queue">
            <ul class="publish-queue-list">
              <li v-for="page in queue" :key="page.id" class="publish-queue-list-item">
                <i
                  :class="{
                    'el-icon-fa-fw': true,
                    'el-icon-fa-circle-notch el-icon-fa-spin': !page.isProcessed,
                    'el-icon-fa-check': page.isProcessed && !page.errorCode,
                    'el-icon-fa-times': page.isProcessed && page.errorCode
                  }"
                />
                {{ page.name }}
                <small class="description">{{ page.path }}</small>
                <div v-if="page.errorCode" class="error">
                  <small>{{ getErrorMessage(page.errorCode) }}</small>
                </div>
              </li>
              <li v-for="region in unpublishedRegions" :key="region.id" class="publish-queue-list-item">
                <i class="el-icon-fa-fw el-icon-fa-times" />
                <code>#{{ region.id }}</code>
                <div class="error">
                  <small>{{ $t('Failed to locate content region.') }}</small>
                </div>
              </li>
            </ul>
            <el-button type="primary" @click="$emit('close')">
              {{ $t('Done') }}
            </el-button>
          </div>
        </transition-group>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.publish-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba($--color-gray-90, 0.9);

  // Slide transition
  .slide-enter-active,
  .slide-leave-active {
    transition: transform 0.5s, opacity 0.5s;
  }

  .slide-enter,
  .slide-leave-to {
    opacity: 0;
    transform: translateY(10rem);
  }

  .publish-progress-body {
    width: 15rem;
    text-align: center;
    color: $--color-gray-20;
  }

  .publish-progress-bar {
    transform: translate3d(0, 0, 0); // prevent transition artifacts in Chrome
    transition: 0.5s transform;
    margin-bottom: 1rem;
  }

  .publish-queue {
    line-height: 1.4;
    text-align: left;
    transition: 0.5s transform;

    .publish-queue-list {
      max-height: 20rem;
      overflow: auto;
      background: rgba($--color-black, 0.5);
      border-radius: 0.2rem;
      list-style: none;
      padding: 1rem;
      margin: 0 0 1rem 0;

      @media screen and (max-height: 700px) {
        max-height: calc(100vh - 300px);
      }

      @media screen and (max-height: 400px) {
        display: none;
      }
    }

    .publish-queue-list-item {
      position: relative;
      padding: 0 0 0 1.5rem;
      margin: 0 0 0.5rem 0;

      i {
        position: absolute;
        top: 0.25rem;
        left: 0;
        color: $--color-gray-70;

        &.el-icon-fa-check {
          color: $--color-success;
        }

        &.el-icon-fa-times {
          color: $--color-danger;
        }
      }

      .description {
        font-size: $--font-size-small;
        color: $--color-gray-70;
        display: block;
      }

      .error {
        font-size: $--font-size-small;
        color: $--color-danger;
      }
    }

    .el-button {
      width: 100%;
    }
  }

  /deep/ .el-progress-circle {
    transition: 0.5s width, 0.5s height;
  }

  /deep/ .el-progress__text {
    color: $--color-gray-20;
  }

  &.fade-enter-active,
  &.fade-leave-active {
    transition: transform 0.5s, opacity 0.5s;
  }

  &.fade-enter,
  &.fade-leave-to {
    opacity: 0;
    transform: scale(2);
  }
}
</style>
